import React, { ReactNode, useCallback, useEffect, useState } from "react";

import moment from "moment";

import {
  getDatePickerDisabledRange,
  getEnvironment,
  inputStateForValue,
  isImpersonating,
  isOrganicFertilizerNotModelReady,
  useFeatureFlag,
  useGetFarms,
  usePracticeManagementOrganicFertilizerParameter,
  useTranslation,
} from "utils";

import { CurrencyCode, FEATURE_FLAGS, HARD_INPUT_STATES, HARVEST_UNIT_VALUE, INPUT_STATE, getUnitDisplayLabel, toTonnesPerHa } from "consts";

import {
  FERTILIZER_ORGANIC_TYPE,
  FERTILIZER_TYPE,
  FERTILIZER_UNITS,
  FertilizerApplication,
  FertilizerType,
  FertilizersResponse,
  ORGANIC_AMOUNT_UNITS,
  OrganicAmountUnit,
  PracticeFertilizerApplication,
  PracticeFertilizerMineral,
  PracticeFertilizerOrganic,
  WithInjectedName,
  ZERO_UUID,
  resolveNValueUnitTypeFromOrganicFertAmountUnit,
} from "models";

import {
  AlertBanner,
  Flex,
  Form,
  IconButton,
  InputNumber,
  InputNumberWithUnitSelector,
  Option,
  RadioChangeEvent,
  RangePicker,
  SanityCheck,
  Select,
  TextArea,
  Typography,
} from "components";

import { CustomKlimRadioGroupWithPayouts } from "pages/app/all-field-view/components/forms/fertilizers/CustomKlimRadioGroupWithPayouts";
import { MineralFertilizerOptionLabel } from "pages/app/all-field-view/components/forms/fertilizers/MineralFertilizerOptionLabel";
import { CALENDAR_YEAR_END, CALENDAR_YEAR_START } from "pages/app/all-field-view/constants/cropForm";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import { getHasSubmissionForSoilPlus3StartYear, getIsSoilPlus3DataCollectionEditable } from "pages/app/all-field-view/utils/dataEntryUtils";
import { InputRequirementConfig } from "pages/app/all-field-view/utils/getInputRequirementConfig";

type OrganicOrMineralFertilizerFormProps = {
  chosenFertilizerType: FertilizerType;
  fertilizers: FertilizersResponse;
  existingFertilizerApplication: FertilizerApplication;
  index: number;
  onRemove: () => void;
  // bulk flows pass a tag naming which of the selected crops this entry belongs to; nothing renders without it
  renderEntryTag?: (practice: FertilizerApplication) => React.ReactNode;
  // bulk flows may name an entry differently, e.g. "New Application" for one that exists on no crop yet
  renderEntryTitle?: (practice: FertilizerApplication) => React.ReactNode;
  // locks every input of the entry, whatever its own verification state allows — for a caller that may not write to it at all
  isReadOnly?: boolean;
  onUpdate?: (index: number, values: Partial<FertilizerApplication>) => void;
  recommendedSeedingDate?: string;
  currency?: CurrencyCode;
  requirementConfig?: InputRequirementConfig | null;
  extraContent?: ReactNode;
};

const createOption = ({
  index,
  option,
  groupName,
  isMineral,
}: {
  index: number;
  option: WithInjectedName<PracticeFertilizerApplication | PracticeFertilizerMineral | PracticeFertilizerOrganic>;
  groupName: string;
  isMineral: boolean;
}) => (
  <Option
    data-testid={`add-edit-fertilizer-form-selection-${groupName}-option-${index}`}
    key={`${groupName}-${option.id}`}
    label={[option.name]}
    value={option.id}
  >
    <MineralFertilizerOptionLabel
      name={option.name}
      hasUreaseInhibitor={isMineral ? (option as WithInjectedName<PracticeFertilizerMineral>).hasUreaseInhibitor : undefined}
      hasNitrificationInhibitor={isMineral ? (option as WithInjectedName<PracticeFertilizerMineral>).hasNitrificationInhibitor : undefined}
      hasLowCarbonEmissions={isMineral ? (option as WithInjectedName<PracticeFertilizerMineral>).hasLowCarbonEmissions : undefined}
    />
  </Option>
);

export const OrganicOrMineralFertilizerForm = ({
  chosenFertilizerType,
  fertilizers,
  existingFertilizerApplication,
  index,
  onRemove,
  renderEntryTag,
  renderEntryTitle,
  isReadOnly = false,
  recommendedSeedingDate,
  currency,
  extraContent,
}: OrganicOrMineralFertilizerFormProps) => {
  const { currentFarm } = useGetFarms();
  const { t } = useTranslation(["modules", "allFieldView"]);
  const [applicationTypeId, setApplicationTypeId] = useState<string | null>(() => existingFertilizerApplication.applicationType ?? null);
  const [fertilizerId, setFertilizerId] = useState<string | null>(() => existingFertilizerApplication.assignedPracticeId ?? null);
  const [isSelfProduced, setIsSelfProduced] = useState<boolean | null>(existingFertilizerApplication.selfProduced ?? null);
  const [amount, setAmount] = useState<number | null>(() => existingFertilizerApplication.amount ?? null);
  const [amountUnit, setAmountUnit] = useState<OrganicAmountUnit | null>(() => (existingFertilizerApplication.amountUnit as OrganicAmountUnit | null) ?? null);
  const [nValue, setNValue] = useState<number | null>(() => existingFertilizerApplication.nValue ?? null);
  const [committedNValue, setCommittedNValue] = useState<number | null>(existingFertilizerApplication.nValue ?? null);
  const [pickerKey, setPickerKey] = useState(`${chosenFertilizerType}-${index}`);

  const form = Form.useFormInstance();

  const selectedOrganicFertilizer = fertilizers.organic?.find((f) => f.id === fertilizerId);
  const fertilizerParameterQuery = usePracticeManagementOrganicFertilizerParameter(selectedOrganicFertilizer?.id ?? "");
  const nValueBounds = fertilizerParameterQuery.data?.nitrogenContentKgPerTon;
  const nValueInputState = committedNValue ? inputStateForValue(committedNValue, nValueBounds) : INPUT_STATE.expected;
  const isNValueImplausible = HARD_INPUT_STATES.includes(nValueInputState);
  const amountBounds = fertilizerParameterQuery.data?.amountAppliedTonnesPerHectare;
  // The bounds are in t/ha, but the farmer may enter the amount in any organic unit
  const amountInTonnesPerHa = amount && amountUnit ? toTonnesPerHa(amount, amountUnit) : null;
  const { isEnabled: isOrganicFertilizerSourcesEnabled } = useFeatureFlag(FEATURE_FLAGS.ORGANIC_FERTILIZER_SOURCES);
  const { submissionStatusValue } = useSubmissionStatus();

  const isAdminOrImpersonator = getEnvironment().admin || isImpersonating();
  const lastSubmissionYear = submissionStatusValue?.lastSubmissionYear;
  const isDisabled =
    isReadOnly ||
    (getIsSoilPlus3DataCollectionEditable(existingFertilizerApplication.soilPlus3DataCollection, lastSubmissionYear)
      ? false
      : existingFertilizerApplication.isConfirmed && !isAdminOrImpersonator);

  const hasSubmissionForSoilPlus3StartYear = getHasSubmissionForSoilPlus3StartYear(lastSubmissionYear);

  const isApplicationDatesDisabled = (): boolean => {
    if (isReadOnly) return true;
    if (!existingFertilizerApplication.endDate) return false;
    if (getIsSoilPlus3DataCollectionEditable(existingFertilizerApplication.applicationDatesSoilPlus3DataCollection, lastSubmissionYear)) return false;

    return existingFertilizerApplication.isConfirmed && !isAdminOrImpersonator && hasSubmissionForSoilPlus3StartYear;
  };

  const handleSelfProducedChange = useCallback((e: RadioChangeEvent) => {
    setIsSelfProduced(!!e.target.value);
  }, []);

  const handleProductNameChange = (nextFertilizerId: string) => {
    const nextOrganicFertilizer = fertilizers.organic.find((fert) => fert.id === nextFertilizerId);

    setFertilizerId(nextFertilizerId);
    setCommittedNValue(null); // Unset this so the farmer has to enter it
    setAmount(null); // Unset this so the farmer has to enter it
    form.setFieldValue([`${chosenFertilizerType}-${index}`, "sanityNoteNitrogen"], undefined);

    if (nextOrganicFertilizer?.type) {
      const nextUnit = nextOrganicFertilizer.type === FERTILIZER_ORGANIC_TYPE.MANURE ? FERTILIZER_UNITS.CUBIC_METERS : FERTILIZER_UNITS.TONNES;
      setAmountUnit(nextUnit);
      form.setFieldValue([`organic-${index}`, "amountUnit"], nextUnit); // Local to this form element
    }
  };

  useEffect(() => {
    // Force the RangePicker to re-evaluate defaultValue by remounting it whenever recommendedSeedingDate changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.
    setPickerKey((prevState) => prevState + 1);
  }, [recommendedSeedingDate]);

  // Pre-populate the saved sanityNoteNitrogen into the form store so the note for the non-visible
  // SanityCheck (e.g. nitrogenBelowRange when N is currently high) survives on save.
  // Also sync committedNValue so the sanity-check banner reflects the correct state when
  // the parent swaps existingFertilizerApplication without remounting this component.
  useEffect(() => {
    form.setFieldValue([`${chosenFertilizerType}-${index}`, "sanityNoteNitrogen"], existingFertilizerApplication.sanityNoteNitrogen);
    setCommittedNValue(existingFertilizerApplication.nValue ?? null);
  }, [existingFertilizerApplication.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="margin-bottom-150">
        <Flex justify="space-between" align="center" className="margin-bottom-100">
          <Flex align="center" gap="0.5rem">
            <Typography variant="h5" className="fertilizers-title">
              {" "}
              {renderEntryTitle?.(existingFertilizerApplication) ?? t("allFieldView:fertilizerForm.application", { number: index + 1 })}
            </Typography>
            {renderEntryTag?.(existingFertilizerApplication)}
          </Flex>
          <IconButton
            type="tertiary"
            size="small"
            iconName="tabler/trash"
            dataTestId="fertilizers-body-container-delete-icon"
            onClick={onRemove}
            disabled={isDisabled}
          />
        </Flex>

        <div className="fertilizers-input-container">
          <Form.Item hidden name={[`${chosenFertilizerType}-${index}`, "id"]} initialValue={existingFertilizerApplication.id} />
          <Form.Item hidden name={[`${chosenFertilizerType}-${index}`, "type"]} initialValue={existingFertilizerApplication.type} />
          {/* Product Name */}
          <Select
            label={t("modules:bauEntryDrawer.fertilizer.productName")}
            name={[`${chosenFertilizerType}-${index}`, "assignedPracticeId"]}
            initialValue={existingFertilizerApplication.assignedPracticeId}
            rules={[{ message: "", required: true }]}
            showSearch
            dataTestId={`fertilizer-select-${index}-${chosenFertilizerType}`}
            value={fertilizerId}
            onChange={handleProductNameChange}
            disabled={isDisabled}
          >
            {chosenFertilizerType &&
              fertilizers[chosenFertilizerType]
                .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                .filter((fertilizer) => {
                  const isSelectedValue = fertilizer.id === existingFertilizerApplication.assignedPracticeId;
                  const isInhibited =
                    chosenFertilizerType === FERTILIZER_TYPE.MINERAL &&
                    !!(
                      (fertilizer as WithInjectedName<PracticeFertilizerMineral>).hasUreaseInhibitor ||
                      (fertilizer as WithInjectedName<PracticeFertilizerMineral>).hasNitrificationInhibitor
                    );

                  // If the initial value is of a disabled or inhibited element we want to render it,
                  // all other disabled or inhibited ones not
                  return fertilizer.id !== ZERO_UUID && (fertilizer.enabled || isSelectedValue) && (!isInhibited || isSelectedValue);
                })
                .map((fertilizer, index: number) =>
                  createOption({ index: index, option: fertilizer, groupName: "all", isMineral: chosenFertilizerType === FERTILIZER_TYPE.MINERAL })
                )}
          </Select>

          {isOrganicFertilizerNotModelReady(selectedOrganicFertilizer, currentFarm?.carbonProgram) && (
            <AlertBanner
              type="info-highlighted"
              iconName="tabler/info-circle"
              dataTestId="crop-not-model-ready-alert"
              description={t("allFieldView:fertilizerForm.fertilizerNotModelReady")}
            />
          )}

          {/* Was it produced on this farm? */}
          {chosenFertilizerType === FERTILIZER_TYPE.ORGANIC && (
            <Flex className="row-space-between" justify="space-between" align="center" gap="1rem">
              <Typography variant="paragraph-m">{t("modules:bauEntryDrawer.fertilizer.selfProduced.title")}</Typography>

              <Form.Item
                name={[`${chosenFertilizerType}-${index}`, "selfProduced"]}
                initialValue={existingFertilizerApplication.selfProduced}
                rules={[{ message: "", required: true }]}
                required
              >
                <CustomKlimRadioGroupWithPayouts
                  value={isSelfProduced}
                  onChange={handleSelfProducedChange}
                  dataTestId="add-edit-fertilizer-form-self-produced-radio-group"
                  className="fertilizers-radio"
                  currency={currency}
                  disabled={isDisabled}
                />
              </Form.Item>
            </Flex>
          )}

          {isOrganicFertilizerSourcesEnabled && chosenFertilizerType === FERTILIZER_TYPE.ORGANIC && isSelfProduced != null && (
            <AlertBanner type="recommended" description={t("allFieldView:fertilizerForm.selfProduced.infoBanner")} />
          )}

          <RangePicker
            key={pickerKey}
            dataTestId={`add-edit-fertilizer-form-date-range-${existingFertilizerApplication.assignedPracticeId}-picker`}
            className="harvest-form-datepicker"
            format={["DD.MM.YYYY"]}
            initialValue={[
              existingFertilizerApplication.startDate ? moment(existingFertilizerApplication.startDate) : null,
              existingFertilizerApplication.endDate ? moment(existingFertilizerApplication.endDate) : null,
            ]}
            defaultPickerValue={recommendedSeedingDate ? [moment(recommendedSeedingDate), moment(recommendedSeedingDate)] : undefined}
            label={t("allFieldView:fertilizerForm.dateRange")}
            name={[`${chosenFertilizerType}-${index}`, "dateRange"]}
            rules={[
              { message: "", required: true },
              {
                validator: (_, value: moment.Moment[] | null) => {
                  if (!value?.[1]) {
                    return Promise.reject(new Error(t("allFieldView:fertilizerForm.errorMessages.dateRangeRequired")));
                  }
                  return Promise.resolve();
                },
              },
            ]}
            // Once submitted, the date can still be changed within the respective calendar year.
            disabledDate={(current) =>
              getIsSoilPlus3DataCollectionEditable(
                existingFertilizerApplication.soilPlus3DataCollection || existingFertilizerApplication.applicationDatesSoilPlus3DataCollection,
                lastSubmissionYear
              )
                ? false
                : !isAdminOrImpersonator &&
                  existingFertilizerApplication.isConfirmed &&
                  !!existingFertilizerApplication.year &&
                  getDatePickerDisabledRange({
                    current,
                    startDate: `${CALENDAR_YEAR_START}${existingFertilizerApplication.year}`,
                    endDate: `${CALENDAR_YEAR_END}${existingFertilizerApplication.year}`,
                  })
            }
            disabled={isApplicationDatesDisabled()}
          />

          {/* Amount */}
          {chosenFertilizerType === FERTILIZER_TYPE.ORGANIC ? (
            <>
              <Form.Item
                noStyle
                name={[`organic-${index}`, "amountUnit"]}
                initialValue={amountUnit}
                rules={[{ required: true, message: t("allFieldView:fertilizerForm.errorMessages.unitRequired", { defaultValue: "The unit is required" }) }]}
                valuePropName="unitValue"
                getValueFromEvent={(_value: number | null, unit: string | null) => unit}
              >
                <InputNumberWithUnitSelector
                  dataTestId={`fertilizer-amount-${index}-organic`}
                  decimalSeparator=","
                  initialValue={existingFertilizerApplication.amount}
                  label={t("allFieldView:fertilizerForm.amount")}
                  name={[`organic-${index}`, "amount"]}
                  min={0.01}
                  rules={[
                    {
                      required: true,
                      message: t("allFieldView:fertilizerForm.errorMessages.amountPerHa", { defaultValue: "The amount per hectare is required" }),
                    },
                  ]}
                  step={0.01}
                  units={[...ORGANIC_AMOUNT_UNITS]}
                  unitLabel={t("allFieldView:fertilizerForm.unit", { defaultValue: "Unit" })}
                  value={amount}
                  unitValue={amountUnit}
                  onChange={(value, unit) => {
                    setAmount(value);
                    setAmountUnit(unit as OrganicAmountUnit);
                  }}
                  disabled={isDisabled}
                />
              </Form.Item>
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const [unitError] = form.getFieldError([`organic-${index}`, "amountUnit"]);
                  return unitError ? <div className="ant-form-item-explain-error unit-error-message">{unitError}</div> : null;
                }}
              </Form.Item>
              {amountInTonnesPerHa !== null && <SanityCheck inputValue={amountInTonnesPerHa} inputRange={amountBounds} unit={HARVEST_UNIT_VALUE.tonnesPerHa} />}
            </>
          ) : (
            <InputNumber
              dataTestId={`fertilizer-amount-${index}-${chosenFertilizerType}`}
              decimalSeparator=","
              initialValue={existingFertilizerApplication.amount}
              label={t("allFieldView:fertilizerForm.amount")}
              name={[`${chosenFertilizerType}-${index}`, "amount"] as unknown as string}
              min={0.01}
              rules={[{ message: "", required: true }]}
              step={0.01}
              suffix={t("allFieldView:fertilizerForm.mineralAmountSuffix")}
              value={amount}
              onChange={(value) => setAmount(value)}
              disabled={isDisabled}
            />
          )}

          {/* N-value */}
          {chosenFertilizerType === FERTILIZER_TYPE.ORGANIC && (
            <>
              <InputNumber
                decimalSeparator=","
                initialValue={existingFertilizerApplication.nValue}
                label={t("allFieldView:fertilizerForm.nValue")}
                min={0.01}
                name={[`${chosenFertilizerType}-${index}`, "nValue"] as unknown as string} // casting this here cos of the shape of InputNumber, while ant accepts array & string
                // Sanity check below menas no error message from the form
                className={isNValueImplausible ? "hide-error-message" : ""}
                validateStatus={isNValueImplausible ? "error" : undefined}
                rules={[{ message: "", required: true }]}
                step={0.01}
                suffix={getUnitDisplayLabel(resolveNValueUnitTypeFromOrganicFertAmountUnit(amountUnit))}
                value={nValue}
                guidanceText={nValueBounds}
                onChange={(value) => {
                  setNValue(value);
                  setCommittedNValue(value);
                }}
                disabled={isDisabled}
              />
              {nValueInputState !== INPUT_STATE.expected && (
                <>
                  <SanityCheck inputValue={committedNValue ?? 0} inputRange={nValueBounds} unit={resolveNValueUnitTypeFromOrganicFertAmountUnit(amountUnit)} />
                  <TextArea
                    dataTestId="add-edit-crop-form-yield-sanity-note"
                    name={[`${chosenFertilizerType}-${index}`, "sanityNoteNitrogen"]}
                    initialValue={existingFertilizerApplication.sanityNoteNitrogen}
                    label={t("allFieldView:fertilizerForm.nitrogenValueNotExpected")}
                    rules={[
                      {
                        required: true,
                        message: t("allFieldView:fertilizerForm.nitrogenValueNotExpected"),
                      },
                    ]}
                    autoSize
                  />
                </>
              )}
            </>
          )}

          {/* Application Type */}
          {chosenFertilizerType === FERTILIZER_TYPE.ORGANIC && (
            <Select
              label={t("modules:bauEntryDrawer.fertilizer.applicationType")}
              name={[`${chosenFertilizerType}-${index}`, "applicationType"]}
              initialValue={applicationTypeId}
              rules={[{ message: "", required: true }]}
              showSearch
              value={applicationTypeId}
              onChange={(value) => setApplicationTypeId(value)}
              disabled={isDisabled}
            >
              {fertilizers.applications
                // If the initial value is of a disabled element we want to render it,
                // all other disabled ones not
                .filter(
                  (application) => application.id !== ZERO_UUID && (application.enabled || application.id === existingFertilizerApplication.applicationType)
                )
                .map((application, index: number) => createOption({ index: index, option: application, groupName: "applications", isMineral: false }))}
            </Select>
          )}
        </div>
      </div>
      {extraContent}
    </>
  );
};
